import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { arcadeApi, arcadeAuth, ArcadeProfile, REASON_LABELS } from '../../services/arcade';
import { RegisterGate } from '../../components/arcade/RegisterGate';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const PerfilPage: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(arcadeAuth.isLoggedIn());
  const [profile, setProfile] = useState<ArcadeProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!loggedIn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    arcadeApi
      .me()
      .then(setProfile)
      .catch(() => {
        // token inválido — limpa e volta ao cadastro
        arcadeAuth.clear();
        setLoggedIn(false);
      })
      .finally(() => setLoading(false));
  }, [loggedIn]);

  return (
    <>
      <Helmet>
        <title>Meu Perfil | Crypto Arcade | {SITE_NAME}</title>
        <meta name="description" content="Seu perfil no Crypto Arcade: saldo de NewsCoins, sequência diária, badges e histórico de pontos." />
        <link rel="canonical" href={`${SITE_URL}/arcade/perfil`} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/arcade" className="hover:text-blue-600 transition">Arcade</Link>
            <span>›</span>
            <span className="text-gray-700">Perfil</span>
          </nav>

          <h1 className="text-3xl font-bold text-gray-900 mb-6">👤 Meu Perfil</h1>

          {!loggedIn ? (
            <RegisterGate onRegistered={() => setLoggedIn(true)} />
          ) : loading ? (
            <div className="text-gray-500 text-center py-10">Carregando perfil...</div>
          ) : profile ? (
            <>
              {/* Resumo */}
              <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{profile.nickname}</p>
                    <p className="text-gray-500 text-sm">
                      Jogando desde {format(new Date(profile.createdAt), "MMMM 'de' yyyy", { locale: ptBR })}
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-600">{profile.balance}</p>
                      <p className="text-xs text-gray-500">NewsCoins</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-600">🔥 {profile.streak.current}</p>
                      <p className="text-xs text-gray-500">sequência (recorde: {profile.streak.best})</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Badges */}
              <div className="bg-white rounded-xl shadow p-6 mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">🏅 Badges</h2>
                {profile.badges.length === 0 ? (
                  <p className="text-gray-500 text-sm">Jogue para conquistar seus primeiros badges!</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {profile.badges.map((badge) => (
                      <div key={badge.code} className="flex items-start gap-3 border border-gray-100 rounded-lg p-3">
                        <span className="text-2xl">{badge.icon}</span>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{badge.name}</p>
                          <p className="text-gray-500 text-xs">{badge.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Histórico */}
              <div className="bg-white rounded-xl shadow p-6 mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">📜 Histórico de pontos</h2>
                {profile.history.length === 0 ? (
                  <p className="text-gray-500 text-sm">Nenhum ponto ainda — jogue seu primeiro jogo!</p>
                ) : (
                  <div className="space-y-2">
                    {profile.history.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2 last:border-0">
                        <span className="text-gray-700">{REASON_LABELS[entry.reason] || entry.reason}</span>
                        <span className="flex items-center gap-3">
                          <span className="text-gray-400 text-xs">
                            {format(new Date(entry.createdAt), 'dd/MM HH:mm')}
                          </span>
                          <span className={`font-bold ${entry.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {entry.amount >= 0 ? '+' : ''}{entry.amount} NC
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400 leading-relaxed">
                NewsCoins são pontos recreativos internos, sem valor monetário — não podem ser compradas,
                vendidas, sacadas ou transferidas, e não são uma criptomoeda. Seu perfil usa apenas um apelido
                e um token salvo neste navegador; nenhum dado pessoal é coletado.
              </p>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};
